import React from "react";
import { EventConnectionWithId } from "@citychallenge/common";
import { ReactiveContext, ConnectSubjectMap } from "./connect";
import { Subject } from "rxjs";
import { evaluateTransform } from "./evaluate-transform";
import { evaluateConditions } from "./evaluate-condition";

export interface EventManagerProps {
  connections: EventConnectionWithId[];
  onUpdateConnection: (connection: EventConnectionWithId) => void;
}

export class EventManager extends React.Component<EventManagerProps> {
  private _scheduledForUpdate = false;
  private _treeStore: { [key: string]: ConnectSubjectMap } = {};
  private _subjects: Subject<any>[] = [];

  public get subjects(): Subject<any>[] {
    return this._subjects;
  }

  public render() {
    return (
      <ReactiveContext.Provider value={this.contextDefault}>
        {this.props.children}
      </ReactiveContext.Provider>
    );
  }

  private updateConnections() {
    try {
      this._updateConnectionsThrows();
    } catch (e) {
      this._scheduledForUpdate = false;
      throw e;
    }
  }
  // Don't call directly, use updateConnections() instead. Because it handles the thrown errors.
  private _updateConnectionsThrows() {
    if (!this._scheduledForUpdate) return;
    if (Object.keys(this._treeStore).length === 0) return; // There is nothing to update
    if (this.props.connections.length === 0) return; // There are no connections to be made

    // Unsubscribe all subscriptions (this is a very rigid implementation, but likely the tree doesn't update often)
    this._subjects.forEach((sub) => sub.complete());
    this._subjects = [];

    // Connect the inlets and outlets with each other
    // Goes like this: from.id.prop.subribe((v) => to.id.prop.next(v))
    for (const connection of this.props.connections) {
      // If the connection was already fired before we will not connect it
      if (connection.completed) continue;

      const fromTreeConnection =
        this._treeStore[connection.from.id || "element"];
      if (!fromTreeConnection) {
        /**
        DefaultLogger.captureException(
          new Error(
            `Connection '${connection.from.id}' => '${connection.to.id}' invalid.` +
              ` From elementId '${connection.from.id}' might be missing?`
          ),
          "warning"
        );
         */
        continue;
      }

      const fromConn = fromTreeConnection[connection.from.prop];
      if (!fromConn) {
        /**
        DefaultLogger.captureException(
          new Error(
            `Connection source '${connection.to.prop}' is invalid. This might be a typo of the 'from' property name?`
          ),
          "warning"
        );
         */
        continue;
      }
      let from = fromConn.subject;

      const origFrom = from;

      if (connection.from.transform) {
        from = evaluateTransform(connection.from.transform, from, () => {
          // Deliberately empty
        });
      }

      if (connection.from.condition) {
        const resolvedArgs = fromConn.arguments || {};

        from = evaluateConditions(
          connection.from.condition,
          from,
          resolvedArgs
        );
      }

      const toTreeConnection = this._treeStore[connection.to.id];
      if (!toTreeConnection) {
        /** 
        DefaultLogger.captureException(
          new Error(
            `Connection '${
              connection.from instanceof Subject
                ? "Subject"
                : connection.from.id
            }` +
              ` => '${connection.to.id}' invalid. To elementId '${connection.to.id}' might be missing?`
          ),
          "warning"
        );*/
        continue;
      }

      const toConn = toTreeConnection[connection.to.prop];
      if (!toConn) {
        /** 
        DefaultLogger.captureException(
          new Error(
            `Connection target ${connection.to.prop} is invalid. This might be a typo of the 'to' property name?`
          ),
          "warning"
        ); */
        continue;
      }
      const to = toConn.subject;

      to.subscribe(null, null, () => {
        this.props.onUpdateConnection({
          ...connection,
          completed: fromConn.repeatable ? false : true,
        });
      });

      // Pipe the result of the from subject to the to subject
      from.subscribe(
        (value: any) => {
          to.next(connection.to.value || value);

          // If not repeatable, stop at the source
          if (!fromConn.repeatable) to.complete();
        },
        undefined,
        () => {
          // For now, all actions are single firing, so we disable to `to` event as well
          if (!fromConn.repeatable && !to.isStopped) to.complete();
        }
      );

      // Append the subscription to the active subscribers
      this._subjects.push(origFrom);
    }

    this._scheduledForUpdate = false;
  }

  /**
   * WARNING: Only use when you want the new connections to take effect immediately,
   * after you have updated connections via the connect() context function.
   * The connect function uses a scheduler based on setTimeout() to collect multiple
   * connections at once and the only fire the updateConnections() one time.
   */
  public updateConnectionsImmediately() {
    this._scheduledForUpdate = true;
    this.updateConnections();
  }

  private connect = (
    id: string,
    inlets: ConnectSubjectMap,
    outlets: ConnectSubjectMap,
    actions?: ConnectSubjectMap
  ) => {
    // Schedule update to connect newly stored inlets/outlets to the from the connections prop

    setTimeout(() => this.updateConnections());
    this._scheduledForUpdate = true;

    const existing = this._treeStore[id] || {};

    this._treeStore[id] = {
      ...existing,
      ...inlets,
      ...outlets,
      ...(actions || {}),
    };
  };

  private contextDefault = {
    connect: this.connect,
  };
}
